import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { AUTH_INVALIDATION_PREFIXES } from '@/lib/query-config';

/**
 * Invalidates all auth-dependent queries when the user changes
 * (login, logout, role switch).
 * 
 * Place this once inside <QueryClientProvider> + <AuthProvider>.
 */
export function useAuthQueryInvalidation() {
  const { user, activeRole } = useAuth();
  const queryClient = useQueryClient();
  const prevUserRef = useRef<string | null>(null);
  const prevRoleRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUserId = user?.id ?? null;
    const currentRole = activeRole ?? null;

    const userChanged = prevUserRef.current !== currentUserId;
    const roleChanged = prevRoleRef.current !== currentRole;

    if (userChanged || roleChanged) {
      // On logout, clear everything
      if (!currentUserId) {
        queryClient.clear();
      } else if (userChanged) {
        // Different user — invalidate all auth-dependent queries
        AUTH_INVALIDATION_PREFIXES.forEach(prefix => {
          queryClient.invalidateQueries({ queryKey: [prefix] });
        });
      }
      // Role switch within same user — invalidate role-dependent data
      else if (roleChanged) {
        queryClient.invalidateQueries({ queryKey: ['appointments'] });
        queryClient.invalidateQueries({ queryKey: ['appointment-history'] });
        queryClient.invalidateQueries({ queryKey: ['unread-messages'] });
      }

      prevUserRef.current = currentUserId;
      prevRoleRef.current = currentRole;
    }
  }, [user?.id, activeRole, queryClient]);
}
